import { TimeAgo } from '@/components/time-ago';
import { ConnectorFlowInstanceJobStatusDTO, ConnectorJobStatus } from '@/models/connectors';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

type JobStatusColumnProps = {
  jobStatus?: ConnectorFlowInstanceJobStatusDTO;
  isCustomConnector: boolean;
};

const THEMES = {
  SUCCESS: { dot: 'bg-green-500', bg: 'bg-green-50', text: 'text-green-700' },
  PARTIAL: { dot: 'bg-amber-500', bg: 'bg-amber-50', text: 'text-amber-700' },
  FAILED: { dot: 'bg-red-500', bg: 'bg-red-50', text: 'text-red-700' },
  ACTIVE: { dot: 'bg-blue-500', bg: 'bg-blue-50', text: 'text-blue-700' },
  NEUTRAL: { dot: 'bg-gray-400', bg: 'bg-gray-50', text: 'text-gray-600' },
  WARNING: { dot: 'bg-orange-500', bg: 'bg-orange-50', text: 'text-orange-700' },
};

const JOB_STATUS_CONFIG: Record<ConnectorJobStatus, { dot: string; bg: string; text: string; label: string }> = {
  SUCCESS: { ...THEMES.SUCCESS, label: 'success' },
  PARTIAL_SUCCESS: { ...THEMES.PARTIAL, label: 'partial' },
  FAILED: { ...THEMES.FAILED, label: 'failed' },
  STARTED: { ...THEMES.ACTIVE, label: 'started' },
  IN_PROGRESS: { ...THEMES.ACTIVE, label: 'running' },
  CANCELLED: { ...THEMES.NEUTRAL, label: 'cancelled' },
  TIMEOUT: { ...THEMES.WARNING, label: 'timeout' },
};

export const JobStatusColumn: FC<JobStatusColumnProps> = ({ jobStatus, isCustomConnector }) => {
  const { t } = useTranslation('status');

  if (!isCustomConnector || !jobStatus) {
    return <NoJobDisplay />;
  }

  const config = JOB_STATUS_CONFIG[jobStatus.status];
  const timestamp = jobStatus.completedAt || jobStatus.createdAt;

  return (
    <div className='flex items-center gap-2'>
      <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium ${config.bg} ${config.text}`}>
        <span className={`inline-block size-1.5 rounded-full ${config.dot}`} />
        {t(`${config.label}`)}
      </span>
      <TimeAgo time={timestamp} isShowTimeElapses />
    </div>
  );
};

const NoJobDisplay = () => {
  return <span className='rounded-md border border-gray-150 px-1.5 py-0.5 text-xs font-medium text-gray-400'>N/A</span>;
};
